import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { concatMap, map, take } from 'rxjs/operators';

import { WidgetService } from '../../main/widgets/widget.service';
import { ObjectState } from '../interfaces/entity';
import { Category } from '../models/lookup-category';
import { ILayoutList, Layout, LayoutTemplate } from '../models/layout';
import { SubjectType } from '../models/subject-type';
import { Template, TEMPLATE_TYPE } from '../models/template';
import { BaseRestService } from './base-rest.service';
import { CategoryService } from './category.service';
import { SubjectTypeService } from './subject-type.service';
import { TemplateService } from './template.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { UserService } from './user.service';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class LayoutService
  extends BaseRestService<Layout, ILayoutList>
  implements OnDestroy
{
  private onSubjectTypeChanged: Subscription;

  constructor(
    http: HttpClient,
    private subjectTypeService: SubjectTypeService,
    private categoryService: CategoryService,
    private widgetService: WidgetService,
    private templateService: TemplateService,
    private userService: UserService,
    protected router: Router
  ) {
    super(LayoutService, http, 'api/layouts', 'layout', router);

    // enable caching
    this.cacheEnabled = true;

    // when subject type is changed, updated any related layouts
    this.onSubjectTypeChanged = this.subjectTypeService.onItemChanged.subscribe(
      (subjectType) => {
        if (subjectType) {
          const s = new SubjectType(subjectType);
          this.cache
            .filter((i) => i.subjectTypeId === subjectType.id)
            .forEach((item) => (item.subjectType = s));
        }
      }
    );
  }

  ngOnDestroy() {
    this.onSubjectTypeChanged?.unsubscribe();
  }

  // get first layout.  Will 404 if id not found
  getLayout(subjectTypeId: number, categoryId?: number): Observable<Layout> {
    const layout$ = this.getAllData().pipe(
      take(1),
      map((data) => {
        this.debug('Get Layout data:', data);
        if (Array.isArray(data)) {
          this.debug('Layout data ok');
          let layout: Layout;

          const cache = [...data];
          if (categoryId) {
            // get layout based on subjecttype and category
            layout = cache.find(
              (x) =>
                x.subjectTypeId === subjectTypeId && x.categoryId === categoryId
            );
          }
          if (!layout) {
            // either no layout specified for subjecttype and category, or category wasn't passed in the first place - attempt to lookup default layout
            layout = cache.find(
              (x) => x.subjectTypeId === subjectTypeId && !x.categoryId
            );
          }
          if (!layout) {
            // no layout found, get the first layout we can find for the specified subject type
            layout = cache.find((x) => x.subjectTypeId === subjectTypeId);
          }
          if (!layout) {
            layout = new Layout({
              subjectTypeId,
              templates: [],
            });
            // this.save(layout);
          } else {
            layout = new Layout(layout);
          }

          // return layout object
          return layout;
        }
        // no layouts, or unable to find a suitable layout, return null
        this.debug('No layout data');
        return null;
      })
    );

    return layout$.pipe(
      concatMap((layout) =>
        forkJoin([
          this.hydrate(layout).pipe(take(1)),
          forkJoin([
            this.getTemplate(layout, TEMPLATE_TYPE.CARD).pipe(take(1)),
            this.getTemplate(layout, TEMPLATE_TYPE.EDIT).pipe(take(1)),
            this.getTemplate(layout, TEMPLATE_TYPE.REFERRAL).pipe(take(1)),
            this.getTemplate(layout, TEMPLATE_TYPE.REFERRALCARD).pipe(take(1)),
            this.getTemplate(layout, TEMPLATE_TYPE.LIST).pipe(take(1)),
            this.getTemplate(layout, TEMPLATE_TYPE.DASHBOARD).pipe(take(1)),
            this.getTemplate(layout, TEMPLATE_TYPE.REFERRALDASHBOARD).pipe(
              take(1)
            ),
          ]),
        ]).pipe(
          take(1),
          map((response) => {
            const result: Layout = response[0];
            return result;
          })
        )
      )
    );
  }

  getTemplate(layout: Layout, templateType: string): Observable<Template> {
    let template: Template;
    let layoutTemplate: LayoutTemplate;
    let userLayoutTemplate: LayoutTemplate;
    const userId = this.userService.getCurrentUserId();

    // check if we can find a corresponding layoutTemplate record
    // userLayoutTemplate = layout.layoutTemplates.find(
    //   (x) =>
    //     x.template &&
    //     x.template.type === templateType &&
    //     x.template.userId === userId
    //);
    layoutTemplate =
      userLayoutTemplate && userLayoutTemplate.template
        ? userLayoutTemplate
        : layout.layoutTemplates.find(
            (x) => x.template && x.template.type === templateType
          );
    if (layoutTemplate && layoutTemplate.template) {
      // found record, get template
      layoutTemplate.template = new Template(null, layoutTemplate.template);
    } else {
      // not found record, we therefore assume doesn't yet exist, so add one to the layout object
      template = new Template(templateType);
      template.userId = userId;
      template.objectState = ObjectState.Added;
      layoutTemplate = layout.addTemplate(template);

      // now check if there is a template from a subjecttype with no category which we can copy from...
      if (layout.categoryId) {
        // create an observable to check/map layoutdata from a similar layout to the current layout, if available
        // and assign to local variable template$.  Observable returns the template object we are interested in...
        const template$ = this.getBySubjectAndCategory(
          layout.subjectTypeId
        ).pipe(
          take(1),
          map((alt) => {
            if (alt) {
              const altTemplate = alt.layoutTemplates.find(
                (x) => x.template && x.template.type === templateType
              );
              if (altTemplate) {
                // found something, so update the layoutdata of our new layouttemplate with the relevant details
                layoutTemplate.template.layoutData =
                  altTemplate.template.layoutData;
              }
            }
            return layoutTemplate.template;
          })
        );

        // ...now chain the observable using concatMap to pass the template to the checkTemplate function
        // and return an observable which returns the template we are interested in
        return template$.pipe(
          concatMap((t: Template) =>
            this.checkTemplate(layout, t).pipe(
              take(1),
              map((checkedTemplate) => {
                return checkedTemplate;
              })
            )
          )
        );
      }
    }
    // if get here then we either found an appropriate template, or we didn't but no category was specified so no potential layoutdata for copy available
    // either way return the checkTemplate observable which returns the template we are interested in
    return this.checkTemplate(layout, layoutTemplate.template);
  }

  private checkTemplate(
    layout: Layout,
    template: Template
  ): Observable<Template> {
    // check template contains some layout items.  If it doesn't then we will need to resort to a default template
    if (template.layoutItems.length > 0) {
      // layout ok, make sure layout and template are correctly associated
      layout.setTemplate(template, template.type);
      return of(template);
    } else {
      return this.widgetService
        .getAvailableSubjectTypeFieldWidgets(
          template.type,
          layout.subjectTypeId,
          [
            'primarylink',
            'linkedsubject',
            'markers',
            'assessmentscore',
            'deadlinedatelastprocessed',
            'useworkingdays',
          ]
        )
        .pipe(
          take(1),
          map((widgetGroups) => {
            template = this.templateService.createDefaultTemplate(
              template.type,
              widgetGroups,
              layout.subjectType.name
            );
            template.objectState = ObjectState.Added;
            layout.setTemplate(template, template.type);
            return template;
          })
        );
    }
  }

  getBySubjectAndCategory(
    subjectTypeId: number,
    categoryId?: number
  ): Observable<Layout> {
    return this.getAllData().pipe(
      take(1),
      map((layouts) => {
        if (categoryId) {
          return layouts.find(
            (x) =>
              x.subjectTypeId === subjectTypeId && x.categoryId === categoryId
          );
        } else {
          return layouts.find(
            (x) => x.subjectTypeId === subjectTypeId && !x.categoryId
          );
        }
      })
    );
  }

  findOrCreateLayout(entity: SubjectType | Category): Layout {
    // for each entity returned check to see if we can find a corresponding layout record...
    let layout: Layout;
    let subjectTypeId;
    if ((entity as Category).subjectTypeId !== undefined) {
      const item = entity as Category;
      // if the entity has a subjectTypeId then is a category, therefore match on subjectTypeId and categoryid
      layout = this.cache.find(
        (x) =>
          x.subjectTypeId === item.subjectTypeId && x.categoryId === item.id
      );
      subjectTypeId = item.subjectTypeId;
    } else {
      // ...otherwise must be a subjectType
      const item = entity as SubjectType;
      // match on id only
      layout = this.cache.find(
        (x) => x.subjectTypeId === item.id && !x.categoryId
      );
    }
    if (!layout) {
      // not found an appropriate layout record, so let's create one
      layout = new Layout({
        subjectTypeId: subjectTypeId || entity.id,
        categoryId: subjectTypeId ? entity.id : undefined,
        // for now don't specify any templates - these will get fleshed out when we call getLayout at a later juncture
        templates: [],
      });
      // we have a new layout - save ourselves some future work and save to the data store
      this.save(layout);
    }
    return layout;
  }

  getAllData(type?: string): Observable<Layout[]> {
    // force refresh of subjecttype and category info held in cache
    if (Array.isArray(this.cache)) {
      this.cache.map((i) => {
        i.subjectType = undefined;
        i.category = undefined;
        this.hydrate(i).pipe(take(1)).subscribe();
      });
    }

    // create an observable to check we have a layout for each subject type, and assign to local variable
    const subjectTypes$ = this.subjectTypeService.getAllData().pipe(
      take(1),
      map((data) => {
        data
          .filter((i) => !i.hideRecord)
          .forEach((item) => {
            this.findOrCreateLayout(item);
          });
        return data;
      })
    );

    // MRD - agreed with PD that layouts for subject type + no category should be auto created, layouts for a specific category should be left to the user to create

    // create an observable to check we have a layout for each category, and assign to local variable
    // const categories$ = this.categoryService.getAllData().pipe(take(1), map((data) => {
    //   data.forEach((item) => {
    //     this.findOrCreateLayout(item);
    //   });
    //   return data;
    // }));

    // return a cold observable which...
    // 1. execute the base getAllData function - any existing layouts are thus available in the cache
    // 2. use concatMap to wait unti the base getAllData observable completes and then pass the result to the inner observable
    // 3. use forkJoin for the inner obervable.  This will wait until both the subjectTyoes and the categories have been checked
    // 4> and then return the result from the cache - this is important as we may have added layouts since getting the initial list of layouts by the base getAllData
    return super.getAllData(type).pipe(
      take(1),
      concatMap((layouts) =>
        forkJoin([subjectTypes$]).pipe(
          take(1),
          map((_) => {
            return this.cache;
          })
        )
      )
    );
  }

  hydrate(item: Layout): Observable<Layout> {
    // define array to hold our observables
    const hydrate$ = [];

    // check properties and add required lookup observables to our array, as required
    if (item.subjectTypeId) {
      if (
        !item.subjectType ||
        item.subjectType.id !== item.subjectTypeId ||
        !item.subjectType.getDataField
      ) {
        hydrate$.push(
          this.subjectTypeService.getById(item.subjectTypeId).pipe(
            take(1),
            map((subjectType) => {
              item.subjectType = new SubjectType(subjectType);
              return item;
            })
          )
        );
      }
    }
    if (item.categoryId) {
      if (!item.category || item.category.id !== item.categoryId) {
        hydrate$.push(
          this.categoryService.getById(item.categoryId).pipe(
            take(1),
            map((category) => {
              item.category = category;
              return item;
            })
          )
        );
      }
    }

    if (hydrate$.length > 0) {
      // use forkJoin to wait until all the observables in our array have completed before returning the item we are interested in
      return forkJoin(hydrate$).pipe(
        take(1),
        map((_) => {
          return item;
        })
      );
    } else {
      // if there are no observables to wait for then return the item we are interested in, as an observable
      return of(item);
    }
  }

  deleteLayout(subjectTypeId: number, categoryId: number) {
    this.getBySubjectAndCategory(subjectTypeId, categoryId)
      .pipe(take(1))
      .subscribe((layout) => {
        if (layout) {
          super.delete(layout);
        }
      });
  }

  save(entity: Layout, successMessage?: string) {
    entity.layoutTemplates.forEach((x) => {
      x.objectState = x.id ? ObjectState.Modified : ObjectState.Added;
      x.template.objectState =
        x.template && x.template.id ? ObjectState.Modified : ObjectState.Added;
    });
    entity.category = undefined;
    return super.save(entity, successMessage);
  }
}
